import { useParams } from "react-router-dom";

import { LoadingState } from "~/common/components";
import {
  CONSULTATIONS_INPUTS_TYPES,
  CONSULTATIONS_QUESTION_TYPES,
} from "~/common/utils";
import {
  INTAKE_QUESTION_TYPES,
  intakeSectionTitle,
} from "~/common/utils/consultations/intake";
import { intakeQuestionDataByCodes } from "~/common/utils/consultations/intake/data";
import {
  CurrentIntakeQuestion,
  IntakeQuestionAnswer,
  IntakeQuestionData,
} from "~/common/utils/consultations/intake/interfaces";
import {
  IntakeCompletedScreen,
  IntakeQuestionRenderer,
} from "~/patients/components/consultations/intake/renderers";
import { ConsultationFormLayout } from "~/patients/components/consultations/shared";
import {
  useAnswerIntakeQuestion,
  useGetCurrentIntakeBundle,
  useGetCurrentIntakeQuestion,
  useGetCurrentPatient,
  useGetIntakePreviousQuestion,
} from "~/patients/hooks";

const transformCurrentQuestion = (currentQuestion: CurrentIntakeQuestion) => {
  if (!currentQuestion.is_custom)
    return intakeQuestionDataByCodes[currentQuestion.code];

  if (
    currentQuestion.question_type ===
    INTAKE_QUESTION_TYPES.supplemental_free_text
  ) {
    return {
      ...currentQuestion,
      title: currentQuestion.question_text,
      type: CONSULTATIONS_QUESTION_TYPES.text,
      inputs: [
        {
          id: "1",
          placeholder: "Enter response",
          value: "supplemental_free_text_response",
          type: CONSULTATIONS_INPUTS_TYPES.text_area,
          required: true,
        },
      ],
    };
  }

  if (
    currentQuestion.question_type ===
    INTAKE_QUESTION_TYPES.supplemental_single_select
  ) {
    return {
      title: currentQuestion.question_text,
      type: INTAKE_QUESTION_TYPES.radio,
      options: currentQuestion.question_options.map((option) => option.text),
    };
  }

  if (
    currentQuestion.question_type ===
    INTAKE_QUESTION_TYPES.supplemental_multi_select
  ) {
    return {
      ...currentQuestion,
      title: currentQuestion.question_text,
      type: CONSULTATIONS_QUESTION_TYPES.multi_check,
      options: currentQuestion.question_options.map((option) => ({
        value: option.text,
        label: option.text,
      })),
    };
  }
};

export const IntakeQuestion = () => {
  const { id } = useParams();
  const { data: patient } = useGetCurrentPatient();
  const consultationId = patient?.consultation?.id;

  const { data: currentQuestion, isLoading } = useGetCurrentIntakeQuestion(id);

  const { data: currentIntakeBundle } = useGetCurrentIntakeBundle();

  const { goBackOneQuestionMutation, isPending: isNavigatingBack } =
    useGetIntakePreviousQuestion();
  const { answerQuestionMutation, isPending: isAnswering } =
    useAnswerIntakeQuestion();

  if (!id) return null;

  const handleGoBack = () => goBackOneQuestionMutation({ id, consultationId });

  if (isLoading) return <LoadingState className="h-screen-calculated" />;

  if (!currentQuestion || !currentIntakeBundle)
    return (
      <IntakeCompletedScreen
        isNavigatingBack={isNavigatingBack}
        handleGoBackFinalScreen={handleGoBack}
      />
    );

  const {
    id: questionId,
    form: intake,
    answer,
    optional,
    options,
  } = currentQuestion;
  const question = transformCurrentQuestion(currentQuestion);
  const title = intakeSectionTitle[intake.code];
  const isFirstQuestion = questionId === intake.first_question_id;

  if (!question) return null;

  const onSubmit = (answer: IntakeQuestionAnswer) => {
    answerQuestionMutation({ id, consultationId, answer });
  };

  return (
    <ConsultationFormLayout
      currentSection={intake.order}
      title={title}
      totalSteps={currentIntakeBundle.total_sections}
    >
      <IntakeQuestionRenderer
        key={currentQuestion.id}
        question={question as IntakeQuestionData}
        handleGoBack={handleGoBack}
        onSubmit={onSubmit}
        disableBack={isFirstQuestion}
        answer={answer}
        isSubmitting={isNavigatingBack || isAnswering}
        currentQuestionOptions={currentQuestion.options}
        isOptional={optional}
        options={options}
      />
    </ConsultationFormLayout>
  );
};
